/**
 * AdminPage.js - Admin view with Users, Analytics, and Import League tabs
 * Users tab: full user management (search, sort, add, edit, delete, plan, role)
 * Analytics tab: platform metrics fetched on-demand from GET /api/admin/analytics
 */

import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateFormat';
import { getPlanDisplayName } from '../utils/planNames';
import './AdminPage.css';

function AdminPage({ onBack }) {
  const { previewTier, setPreviewTier, clearPreviewTier } = useAuth();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedUsers, setExpandedUsers] = useState({});
  const [toast, setToast] = useState(null);
  const [resettingUser, setResettingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importErrors, setImportErrors] = useState(null);
  const [importingLeague, setImportingLeague] = useState(false);
  const [importDragOver, setImportDragOver] = useState(false);

  // User management state
  const [userSearch, setUserSearch] = useState('');
  const [userSort, setUserSort] = useState('newest');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ name: '', email: '', password: '', plan: 'free', isAdmin: false });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: '', email: '', isAdmin: false });
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteAction, setDeleteAction] = useState('delete');
  const [reassignUserId, setReassignUserId] = useState('');
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  // Leagues tab state
  const [adminLeagues, setAdminLeagues] = useState(null);
  const [leaguesLoading, setLeaguesLoading] = useState(false);
  const [leagueSearch, setLeagueSearch] = useState('');
  const [leagueSort, setLeagueSort] = useState('newest');
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [leagueDetailLoading, setLeagueDetailLoading] = useState(false);
  const [leagueDetail, setLeagueDetail] = useState(null);
  const [reassigningLeague, setReassigningLeague] = useState(null);
  const [reassignLeagueUserId, setReassignLeagueUserId] = useState('');
  const [reassignLeagueLoading, setReassignLeagueLoading] = useState(false);
  const [importTargetUserId, setImportTargetUserId] = useState('');

  // Blog tab state
  const [blogArticles, setBlogArticles] = useState(null);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogError, setBlogError] = useState('');
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: '', slug: '', excerpt: '', content: '', published: false });
  const [blogSaving, setBlogSaving] = useState(false);
  const [blogDeleteId, setBlogDeleteId] = useState(null);

  // Sports config tab state
  const [sportsList, setSportsList] = useState(null);
  const [sportsLoading, setSportsLoading] = useState(false);
  const [sportsError, setSportsError] = useState('');
  const [editingSport, setEditingSport] = useState(null);
  const [showSportForm, setShowSportForm] = useState(false);
  const [sportForm, setSportForm] = useState({
    key: '', name: '', scoreLabel: 'Score', officialTitle: 'Referee',
    pointsForWin: 2, pointsForTie: 1, pointsForLoss: 0, allowTies: true,
    winLossOnly: false, supportsBestOf: false, supportsPlayerRatings: false,
    statLabelGF: 'PF', statLabelGA: 'PA', statLabelGD: 'Diff',
    sortOrder: 999, isActive: true
  });
  const [sportSaving, setSportSaving] = useState(false);
  const [sportDeleteId, setSportDeleteId] = useState(null);

  // Migration tab state
  const [migrationSubTab, setMigrationSubTab] = useState('account');
  const [migrationLogs, setMigrationLogs] = useState(null);
  const [migrationLogsLoading, setMigrationLogsLoading] = useState(false);
  // Account migration
  const [acctSourceUserId, setAcctSourceUserId] = useState('');
  const [acctTargetUserId, setAcctTargetUserId] = useState('');
  const [acctDeleteSource, setAcctDeleteSource] = useState(false);
  const [acctPreview, setAcctPreview] = useState(null);
  const [acctPreviewLoading, setAcctPreviewLoading] = useState(false);
  const [acctMigrating, setAcctMigrating] = useState(false);
  // League migration
  const [leagueMigLeagueId, setLeagueMigLeagueId] = useState('');
  const [leagueMigTargetUserId, setLeagueMigTargetUserId] = useState('');
  const [leagueMigPreview, setLeagueMigPreview] = useState(null);
  const [leagueMigPreviewLoading, setLeagueMigPreviewLoading] = useState(false);
  const [leagueMigrating, setLeagueMigrating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (editingUser) {
      setEditUserForm({ name: editingUser.name, email: editingUser.email, isAdmin: editingUser.isAdmin });
    }
  }, [editingUser]);

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = async () => {
    try {
      const response = await api.getAdminUsers();
      setData(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    if (analytics) return;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const response = await api.getAdminAnalytics();
      setAnalytics(response.analytics);
    } catch (err) {
      setAnalyticsError(err.message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadAdminLeagues = async (force = false) => {
    if (adminLeagues && !force) return;
    setLeaguesLoading(true);
    try {
      const response = await api.getAdminLeagues();
      setAdminLeagues(response.leagues);
    } catch (err) {
      showToast('Failed to load leagues: ' + err.message);
    } finally {
      setLeaguesLoading(false);
    }
  };

  const loadSports = async () => {
    setSportsLoading(true);
    setSportsError('');
    try {
      const result = await api.getAdminSports();
      setSportsList(result.sports);
    } catch (err) {
      setSportsError('Failed to load sports: ' + err.message);
    } finally {
      setSportsLoading(false);
    }
  };

  const loadBlogArticles = async () => {
    setBlogLoading(true);
    setBlogError('');
    try {
      const articles = await api.getAdminBlogArticles();
      setBlogArticles(articles);
    } catch (err) {
      setBlogError('Failed to load articles: ' + err.message);
    } finally {
      setBlogLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'analytics') {
      loadAnalytics();
    }
    if (tab === 'leagues') {
      loadAdminLeagues();
    }
    if (tab === 'migrations') {
      loadMigrationLogs();
      loadAdminLeagues();
    }
    if (tab === 'import') {
      setImportFile(null);
      setImportFileName('');
      setImportErrors(null);
      setImportTargetUserId('');
    }
    if (tab === 'blog') {
      loadBlogArticles();
    }
    if (tab === 'sports') {
      loadSports();
    }
  };

  // ============================================
  // Import handlers (unchanged)
  // ============================================

  const handleImportFileSelect = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      showToast('Please select a .json file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        setImportFile(parsed);
        setImportFileName(file.name);
        setImportErrors(null);
      } catch {
        showToast('Invalid JSON file');
        setImportFile(null);
        setImportFileName('');
      }
    };
    reader.readAsText(file);
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    try {
      setImportingLeague(true);
      setImportErrors(null);
      const payload = { ...importFile };
      if (importTargetUserId) {
        payload.targetUserId = importTargetUserId;
      }
      await api.importLeague(payload);
      setImportFile(null);
      setImportFileName('');
      setImportTargetUserId('');
      setAdminLeagues(null); // Force re-fetch on next tab switch
      showToast('League imported successfully!', 'success');
    } catch (err) {
      if (err.validationErrors) {
        setImportErrors(err.validationErrors);
      } else {
        showToast('Failed to import: ' + err.message);
      }
    } finally {
      setImportingLeague(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const template = await api.getImportTemplate();
      const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'league-import-template.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast('Failed to download template: ' + err.message);
    }
  };

  // ============================================
  // Migration handlers
  // ============================================

  const loadMigrationLogs = async () => {
    setMigrationLogsLoading(true);
    try {
      const response = await api.getMigrationLogs();
      setMigrationLogs(response.migrations);
    } catch (err) {
      showToast('Failed to load migration logs: ' + err.message);
    } finally {
      setMigrationLogsLoading(false);
    }
  };

  const handlePreviewAccountMigration = async () => {
    if (!acctSourceUserId || !acctTargetUserId) return;
    setAcctPreviewLoading(true);
    setAcctPreview(null);
    try {
      const response = await api.previewAccountMigration(acctSourceUserId, acctTargetUserId, acctDeleteSource);
      setAcctPreview(response.preview);
    } catch (err) {
      showToast(err.message);
    } finally {
      setAcctPreviewLoading(false);
    }
  };

  const handleExecuteAccountMigration = async () => {
    if (!acctPreview) return;
    const msg = `Transfer ALL data from ${acctPreview.source.name} to ${acctPreview.target.name}?\n\n` +
      `${acctPreview.leagues.length} league(s), ${acctPreview.captains.total} captain(s), ` +
      `${acctPreview.venues.total} venue(s), ${acctPreview.referees.total} referee(s)` +
      (acctDeleteSource ? `\n\nSource user "${acctPreview.source.name}" will be DELETED.` : '');
    if (!window.confirm(msg)) return;

    setAcctMigrating(true);
    try {
      const result = await api.executeAccountMigration(acctSourceUserId, acctTargetUserId, acctDeleteSource);
      showToast(result.message, 'success');
      setAcctSourceUserId('');
      setAcctTargetUserId('');
      setAcctDeleteSource(false);
      setAcctPreview(null);
      loadData();
      loadMigrationLogs();
      setAdminLeagues(null);
    } catch (err) {
      showToast(err.message);
    } finally {
      setAcctMigrating(false);
    }
  };

  const handlePreviewLeagueMigration = async () => {
    if (!leagueMigLeagueId || !leagueMigTargetUserId) return;
    setLeagueMigPreviewLoading(true);
    setLeagueMigPreview(null);
    try {
      const response = await api.previewLeagueMigration(leagueMigLeagueId, leagueMigTargetUserId);
      setLeagueMigPreview(response.preview);
    } catch (err) {
      showToast(err.message);
    } finally {
      setLeagueMigPreviewLoading(false);
    }
  };

  const handleExecuteLeagueMigration = async () => {
    if (!leagueMigPreview) return;
    const p = leagueMigPreview;
    const msg = `Transfer "${p.league.name}" to ${p.targetUser.name}?\n\n` +
      `${p.captains.toCreate} captain(s) will be copied, ${p.captains.toDedup} reused.\n` +
      `${p.venues.toCreate} venue(s) will be copied, ${p.venues.toDedup} reused.\n` +
      `${p.referees.toCreate} referee(s) will be copied, ${p.referees.toDedup} reused.`;
    if (!window.confirm(msg)) return;

    setLeagueMigrating(true);
    try {
      const result = await api.executeLeagueMigration(leagueMigLeagueId, leagueMigTargetUserId);
      showToast(result.message, 'success');
      setLeagueMigLeagueId('');
      setLeagueMigTargetUserId('');
      setLeagueMigPreview(null);
      loadData();
      loadMigrationLogs();
      setAdminLeagues(null);
    } catch (err) {
      showToast(err.message);
    } finally {
      setLeagueMigrating(false);
    }
  };

  // ============================================
  // User management handlers
  // ============================================

  const toggleUser = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Comp subscription state
  const [compingUser, setCompingUser] = useState(null);
  const [compForm, setCompForm] = useState({ plan: 'pro', compedUntil: '', compReason: '' });
  const [compLoading, setCompLoading] = useState(false);

  const handlePlanChange = async (userId, newPlan) => {
    try {
      await api.updateUserPlan(userId, newPlan);
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => u.id === userId ? { ...u, plan: newPlan } : u)
      }));
      showToast(`Plan updated to ${newPlan}`, 'success');
    } catch (err) {
      showToast('Failed to update plan: ' + err.message);
    }
  };

  const handleCompUser = async (e) => {
    e.preventDefault();
    setCompLoading(true);
    try {
      await api.updateUserPlan(compingUser.id, compForm.plan, {
        compedUntil: compForm.compedUntil || null,
        compReason: compForm.compReason || null
      });
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => u.id === compingUser.id ? {
          ...u,
          plan: compForm.plan,
          compedUntil: compForm.compedUntil || null,
          compReason: compForm.compReason || null
        } : u)
      }));
      showToast(`Comp applied: ${getPlanDisplayName(compForm.plan)} plan`, 'success');
      setCompingUser(null);
      setCompForm({ plan: 'pro', compedUntil: '', compReason: '' });
    } catch (err) {
      showToast('Failed to comp user: ' + err.message);
    } finally {
      setCompLoading(false);
    }
  };

  const handleRemoveComp = async (userId) => {
    try {
      await api.updateUserPlan(userId, 'free', { compedUntil: null, compReason: null });
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => u.id === userId ? { ...u, plan: 'free', compedUntil: null, compReason: null } : u)
      }));
      showToast('Comp removed, reverted to Pickup', 'success');
    } catch (err) {
      showToast('Failed to remove comp: ' + err.message);
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    setResettingUser(userId);
    try {
      await api.adminResetPassword(userId);
      showToast(`Reset link sent to ${userEmail}`, 'success');
    } catch (err) {
      showToast('Failed to send reset link: ' + err.message);
    } finally {
      setResettingUser(null);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setAddUserLoading(true);
    try {
      await api.adminCreateUser(addUserForm);
      setShowAddUserModal(false);
      setAddUserForm({ name: '', email: '', password: '', plan: 'free', isAdmin: false });
      loadData();
      showToast('User created successfully', 'success');
    } catch (err) {
      showToast(err.message);
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setEditUserLoading(true);
    try {
      await api.adminUpdateUser(editingUser.id, editUserForm);
      setEditingUser(null);
      loadData();
      showToast('User updated successfully', 'success');
    } catch (err) {
      showToast(err.message);
    } finally {
      setEditUserLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to delete "${deletingUser.name}"? This cannot be undone.`)) return;
    setDeleteUserLoading(true);
    try {
      await api.adminDeleteUser(deletingUser.id, {
        action: deleteAction,
        reassignToUserId: deleteAction === 'reassign' ? reassignUserId : undefined
      });
      setDeletingUser(null);
      setDeleteAction('delete');
      setReassignUserId('');
      loadData();
      showToast('User deleted', 'success');
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleteUserLoading(false);
    }
  };

  // ============================================
  // League management handlers (unchanged)
  // ============================================

  const handleStatusChange = async (leagueId, newStatus) => {
    try {
      const updates = { status: newStatus };
      if (newStatus === 'draft') {
        updates.isPublic = false;
      }
      await api.updateAdminLeague(leagueId, updates);
      loadData();
    } catch (err) {
      showToast('Failed to update status: ' + err.message);
    }
  };

  const handleTogglePublic = async (leagueId, currentStatus, leagueStatus) => {
    if (leagueStatus === 'draft') return;
    try {
      await api.updateAdminLeague(leagueId, { isPublic: !currentStatus });
      loadData();
    } catch (err) {
      showToast('Failed to update public flag: ' + err.message);
    }
  };

  const handleToggleStandings = async (leagueId, currentStatus, isPublic) => {
    if (!isPublic) return;
    try {
      await api.updateAdminLeague(leagueId, { showStandings: !currentStatus });
      loadData();
    } catch (err) {
      showToast('Failed to update standings: ' + err.message);
    }
  };

  const handleDownloadPDF = async (league) => {
    try {
      const response = await api.getAdminLeague(league.id);
      const originalInput = response.league.scheduleData?.originalInput;

      if (!originalInput) {
        showToast('Cannot generate PDF - missing schedule data');
        return;
      }

      const cleanData = {
        leagueName: league.name,
        seasonYear: league.seasonYear,
        sport: league.sport || undefined,
        roundRobinTeams: originalInput.roundRobinTeams,
        roundRobinGroup2Teams: originalInput.roundRobinGroup2Teams || originalInput.dedicatedPairTeams || [],
        gameDates: originalInput.gameDates,
        timeSlots: originalInput.timeSlots,
        status: league.status
      };

      const blob = await api.generatePDF(cleanData);
      const filename = `${league.name.replace(/[^a-z0-9]/gi, '_')}_schedule.pdf`;
      api.downloadFile(blob, filename);
    } catch (err) {
      showToast('Failed to generate PDF: ' + err.message);
    }
  };

  const handleExportLeague = async (league) => {
    try {
      const response = await api.adminExportLeague(league.id);
      const disposition = response.headers['content-disposition'];
      const filenameMatch = disposition && disposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `${league.name}-export.json`;
      api.downloadFile(response.data, filename);
    } catch (err) {
      showToast(err.message || 'Failed to export league');
    }
  };

  const handleDelete = async (leagueId, leagueName) => {
    if (window.confirm(`Are you sure you want to delete "${leagueName}"? This cannot be undone.`)) {
      try {
        await api.deleteAdminLeague(leagueId);
        loadData();
        showToast('League deleted successfully', 'success');
      } catch (err) {
        showToast('Failed to delete league: ' + err.message);
      }
    }
  };

  // ============================================
  // Admin Leagues tab handlers
  // ============================================

  const handleViewLeagueDetail = async (league) => {
    setSelectedLeague(league);
    setLeagueDetailLoading(true);
    setReassigningLeague(null);
    setReassignLeagueUserId('');
    try {
      const response = await api.getAdminLeague(league.id);
      setLeagueDetail(response.league);
    } catch (err) {
      showToast('Failed to load league details: ' + err.message);
      setSelectedLeague(null);
    } finally {
      setLeagueDetailLoading(false);
    }
  };

  const handleReassignLeague = async () => {
    if (!reassignLeagueUserId || !selectedLeague) return;
    setReassignLeagueLoading(true);
    try {
      await api.reassignLeague(selectedLeague.id, reassignLeagueUserId);
      showToast('League reassigned successfully', 'success');
      setReassigningLeague(null);
      setReassignLeagueUserId('');
      setSelectedLeague(null);
      setLeagueDetail(null);
      setAdminLeagues(null);
      loadAdminLeagues(true);
      loadData();
    } catch (err) {
      showToast(err.message);
    } finally {
      setReassignLeagueLoading(false);
    }
  };

  const handleDeleteLeagueFromTab = async (leagueId, leagueName) => {
    if (window.confirm(`Are you sure you want to delete "${leagueName}"? This cannot be undone.`)) {
      try {
        await api.deleteAdminLeague(leagueId);
        setAdminLeagues(null);
        loadAdminLeagues(true);
        loadData();
        showToast('League deleted successfully', 'success');
      } catch (err) {
        showToast('Failed to delete league: ' + err.message);
      }
    }
  };

  const handleExportLeagueFromTab = async (league) => {
    try {
      const response = await api.adminExportLeague(league.id);
      const disposition = response.headers['content-disposition'];
      const filenameMatch = disposition && disposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `${league.name}-export.json`;
      api.downloadFile(response.data, filename);
    } catch (err) {
      showToast(err.message || 'Failed to export league');
    }
  };

  const getFilteredLeagues = () => {
    if (!adminLeagues) return [];
    return adminLeagues
      .filter(l => {
        if (!leagueSearch) return true;
        const q = leagueSearch.toLowerCase();
        return l.name.toLowerCase().includes(q) ||
          (l.owner?.name || '').toLowerCase().includes(q) ||
          (l.owner?.email || '').toLowerCase().includes(q) ||
          (l.city || '').toLowerCase().includes(q) ||
          (l.state || '').toLowerCase().includes(q) ||
          (l.sport || '').toLowerCase().includes(q);
      })
      .sort((a, b) => {
        switch (leagueSort) {
          case 'newest': return new Date(b.createdAt) - new Date(a.createdAt);
          case 'oldest': return new Date(a.createdAt) - new Date(b.createdAt);
          case 'name-asc': return a.name.localeCompare(b.name);
          case 'name-desc': return b.name.localeCompare(a.name);
          case 'most-teams': return (b.teamCount || 0) - (a.teamCount || 0);
          default: return 0;
        }
      });
  };

  // ============================================
  // Filter & sort users
  // ============================================

  const getFilteredUsers = () => {
    if (!data?.users) return [];
    return data.users
      .filter(u => {
        if (!userSearch) return true;
        const q = userSearch.toLowerCase();
        return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        switch (userSort) {
          case 'newest': return new Date(b.createdAt) - new Date(a.createdAt);
          case 'oldest': return new Date(a.createdAt) - new Date(b.createdAt);
          case 'name-asc': return a.name.localeCompare(b.name);
          case 'name-desc': return b.name.localeCompare(a.name);
          case 'last-login': return (b.lastLoginAt ? new Date(b.lastLoginAt) : 0) - (a.lastLoginAt ? new Date(a.lastLoginAt) : 0);
          default: return 0;
        }
      });
  };

  // ============================================
  // Render
  // ============================================

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Loading admin data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>Admin</h1>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const filteredUsers = getFilteredUsers();

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p className="admin-summary">
            {data.totalUsers} users, {data.totalLeagues} leagues
          </p>
        </div>
      </div>

      {/* Preview as Tier */}
      <div className="admin-preview-tier-section">
        <span className="admin-preview-tier-label">Preview as Tier:</span>
        {[
          { tier: 'free', label: 'Pickup' },
          { tier: 'starter', label: 'League' },
          { tier: 'pro', label: 'Commissioner' }
        ].map(({ tier, label }) => (
          <button
            key={tier}
            className={`admin-preview-tier-btn${previewTier === tier ? ' admin-preview-tier-btn-active' : ''}`}
            disabled={previewLoading}
            onClick={async () => {
              setPreviewLoading(true);
              try {
                if (previewTier === tier) {
                  await clearPreviewTier();
                } else {
                  await setPreviewTier(tier);
                }
              } catch (_) { /* ignore */ }
              setPreviewLoading(false);
            }}
          >
            {label}
          </button>
        ))}
        {previewTier && (
          <button
            className="admin-preview-tier-btn admin-preview-tier-btn-exit"
            disabled={previewLoading}
            onClick={async () => {
              setPreviewLoading(true);
              try { await clearPreviewTier(); } catch (_) {}
              setPreviewLoading(false);
            }}
          >
            Exit Preview
          </button>
        )}
      </div>

      {toast && (
        <div className={`admin-toast admin-toast-${toast.type}`}>
          {toast.message}
          <button className="admin-toast-close" onClick={() => setToast(null)}>x</button>
        </div>
      )}

      {/* Tab Bar */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '1.5rem',
        borderBottom: '2px solid #e2e8f0'
      }}>
        {['users', 'leagues', 'sports', 'analytics', 'migrations', 'import', 'blog'].map(tab => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab ? '3px solid #667eea' : '3px solid transparent',
              color: activeTab === tab ? '#667eea' : '#718096',
              fontWeight: activeTab === tab ? 700 : 500,
              fontSize: '1rem',
              cursor: 'pointer',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            {tab === 'users' ? 'Users' : tab === 'leagues' ? 'Leagues' : tab === 'sports' ? 'Sports' : tab === 'analytics' ? 'Analytics' : tab === 'migrations' ? 'Migrations' : tab === 'blog' ? 'Blog' : 'Import League'}
          </button>
        ))}
      </div>

      {/* ============================================ */}
      {/* Users Tab */}
      {/* ============================================ */}
      {activeTab === 'users' && (
        <div>
          {/* Search, Sort, Add bar */}
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={{
                flex: 1,
                minWidth: 200,
                padding: '0.5rem 0.75rem',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.9rem',
                background: '#fff'
              }}
            />
            <select
              value={userSort}
              onChange={(e) => setUserSort(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                fontSize: '0.85rem',
                background: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="last-login">Last Login</option>
            </select>
            <button
              onClick={() => setShowAddUserModal(true)}
              style={{
                padding: '0.5rem 1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              + Add User
            </button>
          </div>

          {/* User count */}
          {userSearch && (
            <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '0.75rem' }}>
              {filteredUsers.length} of {data.users.length} users
            </div>
          )}

          {/* User list */}
          <div className="users-list">
            {filteredUsers.map(user => (
              <div key={user.id} className="user-card">
                <div className="user-card-header" onClick={() => toggleUser(user.id)}>
                  <div className="user-info">
                    <h3>
                      {user.name}
                      {user.isAdmin && (
                        <span style={{
                          marginLeft: 8,
                          background: '#667eea',
                          color: '#fff',
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          verticalAlign: 'middle'
                        }}>
                          Admin
                        </span>
                      )}
                    </h3>
                    <span className="user-email">{user.email}</span>
                  </div>
                  <div className="user-meta">
                    <select
                      value={user.plan || 'free'}
                      onChange={(e) => handlePlanChange(user.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        backgroundColor: (user.plan || 'free') === 'pro' ? '#667eea' :
                                         (user.plan || 'free') === 'starter' ? '#764ba2' : '#e2e8f0',
                        color: (user.plan || 'free') === 'free' ? '#4a5568' : '#fff',
                      }}
                    >
                      <option value="free">Pickup</option>
                      <option value="starter">League</option>
                      <option value="pro">Commissioner</option>
                    </select>
                    {user.compedUntil && new Date(user.compedUntil) > new Date() && (
                      <span
                        onClick={(e) => e.stopPropagation()}
                        title={`Comp: ${user.compReason || 'No reason'}\nExpires: ${formatDate(user.compedUntil)}`}
                        style={{
                          background: '#38a169',
                          color: '#fff',
                          padding: '2px 6px',
                          borderRadius: 10,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          cursor: 'default',
                          letterSpacing: '0.02em'
                        }}
                      >
                        COMPED
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCompingUser(user);
                        setCompForm({
                          plan: user.plan || 'pro',
                          compedUntil: user.compedUntil ? new Date(user.compedUntil).toISOString().split('T')[0] : '',
                          compReason: user.compReason || ''
                        });
                      }}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #38a169',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        backgroundColor: '#f0fff4',
                        color: '#38a169',
                        fontWeight: 500
                      }}
                    >
                      Comp
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingUser(user); }}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #667eea',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        backgroundColor: '#f0f4ff',
                        color: '#667eea',
                        fontWeight: 500
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingUser(user);
                        setDeleteAction('delete');
                        setReassignUserId('');
                      }}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e53e3e',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        backgroundColor: '#fff5f5',
                        color: '#e53e3e',
                        fontWeight: 500
                      }}
                    >
                      Delete
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleResetPassword(user.id, user.email); }}
                      disabled={resettingUser === user.id}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0',
                        fontSize: '0.75rem',
                        cursor: resettingUser === user.id ? 'not-allowed' : 'pointer',
                        backgroundColor: '#f7fafc',
                        color: '#4a5568',
                        opacity: resettingUser === user.id ? 0.6 : 1,
                      }}
                    >
                      {resettingUser === user.id ? 'Sending...' : 'Reset Pwd'}
                    </button>
                    <span className="league-count">{user.leagueCount} league{user.leagueCount !== 1 ? 's' : ''}</span>
                    <span className="join-date">
                      {user.lastLoginAt ? `Last login ${formatDate(user.lastLoginAt)}` : 'Never logged in'}
                    </span>
                    <span className="expand-icon">{expandedUsers[user.id] ? '\u25B2' : '\u25BC'}</span>
                  </div>
                </div>

                {expandedUsers[user.id] && (
                  <div className="user-leagues">
                    {user.leagues.length === 0 ? (
                      <p className="no-leagues">No leagues created</p>
                    ) : (
                      <div className="admin-leagues-list">
                        {user.leagues.map(league => (
                          <div key={league.id} className="admin-league-card">
                            <div className="admin-league-header">
                              <h4>{league.name}</h4>
                              <select
                                value={league.status}
                                onChange={(e) => handleStatusChange(league.id, e.target.value)}
                                className="admin-status-select"
                              >
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="completed">Completed</option>
                              </select>
                            </div>

                            <div className="admin-league-details">
                              <span className="admin-league-detail">
                                <strong>Season:</strong> {league.seasonYear}
                              </span>
                              {league.sport && (
                                <span className="admin-league-detail">
                                  <strong>Sport:</strong> {league.sport}
                                </span>
                              )}
                              <span className="admin-league-detail">
                                <strong>Teams:</strong> {league.teamCount}
                              </span>
                              <span className="admin-league-detail">
                                <strong>Created:</strong> {formatDate(league.createdAt)}
                              </span>
                            </div>

                            <div className="admin-league-toggles">
                              <label className={`admin-toggle${league.status === 'draft' ? ' admin-toggle-disabled' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={league.isPublic}
                                  onChange={() => handleTogglePublic(league.id, league.isPublic, league.status)}
                                  disabled={league.status === 'draft'}
                                />
                                <span>Public</span>
                              </label>
                              <label className={`admin-toggle${!league.isPublic ? ' admin-toggle-disabled' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={league.isPublic && league.showStandings !== false}
                                  onChange={() => handleToggleStandings(league.id, league.showStandings !== false, league.isPublic)}
                                  disabled={!league.isPublic}
                                />
                                <span>Standings</span>
                              </label>
                            </div>

                            <div className="admin-league-actions">
                              <button
                                onClick={() => handleDownloadPDF(league)}
                                className="admin-action-btn admin-action-pdf"
                              >
                                PDF
                              </button>
                              <button
                                onClick={() => handleExportLeague(league)}
                                className="admin-action-btn admin-action-export"
                              >
                                Export
                              </button>
                              <button
                                onClick={() => handleDelete(league.id, league.name)}
                                className="admin-action-btn admin-action-delete"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {filteredUsers.length === 0 && userSearch && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>
                No users matching "{userSearch}"
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Add User Modal */}
      {/* ============================================ */}
      {showAddUserModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: '1.5rem',
            maxWidth: 440,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 1rem', color: '#333' }}>Add User</h3>
            <form onSubmit={handleCreateUser}>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>
                  Name *
                </label>
                <input
                  value={addUserForm.name}
                  onChange={(e) => setAddUserForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={addUserForm.email}
                  onChange={(e) => setAddUserForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>
                  Temporary Password *
                </label>
                <input
                  type="text"
                  value={addUserForm.password}
                  onChange={(e) => setAddUserForm(prev => ({ ...prev, password: e.target.value }))}
                  required
                  minLength={6}
                  placeholder="At least 6 characters"
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
                <small style={{ color: '#718096', fontSize: '0.78rem' }}>
                  User will be required to change this on first login
                </small>
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>
                  Plan
                </label>
                <select
                  value={addUserForm.plan}
                  onChange={(e) => setAddUserForm(prev => ({ ...prev, plan: e.target.value }))}
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                >
                  <option value="free">Free</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                </select>
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#4a5568', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={addUserForm.isAdmin}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, isAdmin: e.target.checked }))}
                  />
                  <span style={{ fontWeight: 500 }}>Grant admin access</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setAddUserForm({ name: '', email: '', password: '', plan: 'free', isAdmin: false });
                  }}
                  style={{
                    padding: '0.6rem 1.25rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    background: '#fff',
                    color: '#4a5568',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addUserLoading}
                  style={{
                    padding: '0.6rem 1.25rem',
                    border: 'none',
                    borderRadius: 8,
                    background: addUserLoading ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    cursor: addUserLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}
                >
                  {addUserLoading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Edit User Modal */}
      {/* ============================================ */}
      {editingUser && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: '1.5rem',
            maxWidth: 440,
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 1rem', color: '#333' }}>Edit User</h3>
            <form onSubmit={handleEditUser}>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>
                  Name
                </label>
                <input
                  value={editUserForm.name}
                  onChange={(e) => setEditUserForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#4a5568', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editUserForm.isAdmin}
                    onChange={(e) => {
                      if (e.target.checked && !editingUser.isAdmin) {
                        if (!window.confirm(`Are you sure you want to grant admin access to ${editingUser.name}? Admins can manage all users and data.`)) return;
                      }
                      setEditUserForm(prev => ({ ...prev, isAdmin: e.target.checked }));
                    }}
                  />
                  <span style={{ fontWeight: 500 }}>Admin access</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  style={{
                    padding: '0.6rem 1.25rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    background: '#fff',
                    color: '#4a5568',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editUserLoading}
                  style={{
                    padding: '0.6rem 1.25rem',
                    border: 'none',
                    borderRadius: 8,
                    background: editUserLoading ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    cursor: editUserLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}
                >
                  {editUserLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Delete User Modal */}
      {/* ============================================ */}
      {deletingUser && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: '1.5rem',
            maxWidth: 480,
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 0.75rem', color: '#333' }}>
              Delete User: {deletingUser.name}
            </h3>
            <p style={{ color: '#718096', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
              {deletingUser.email}
            </p>

            {deletingUser.leagueCount > 0 ? (
              <div>
                <p style={{ color: '#e53e3e', fontWeight: 500, fontSize: '0.9rem', margin: '0.75rem 0' }}>
                  This user has {deletingUser.leagueCount} league{deletingUser.leagueCount !== 1 ? 's' : ''}. Choose how to handle them:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '12px 0 16px' }}>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="deleteAction"
                      value="delete"
                      checked={deleteAction === 'delete'}
                      onChange={() => setDeleteAction('delete')}
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong style={{ fontSize: '0.9rem' }}>Delete everything</strong>
                      <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '0.82rem' }}>
                        Permanently delete the user and all their leagues, teams, games, and data.
                      </p>
                    </div>
                  </label>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="deleteAction"
                      value="reassign"
                      checked={deleteAction === 'reassign'}
                      onChange={() => setDeleteAction('reassign')}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: '0.9rem' }}>Reassign to another user</strong>
                      <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '0.82rem' }}>
                        Transfer all leagues, venues, captains, and referees to a different user, then delete this account.
                      </p>
                      {deleteAction === 'reassign' && (
                        <select
                          value={reassignUserId}
                          onChange={(e) => setReassignUserId(e.target.value)}
                          style={{
                            marginTop: 8,
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            fontSize: '0.85rem',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="">Select a user...</option>
                          {data.users
                            .filter(u => u.id !== deletingUser.id)
                            .map(u => (
                              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                            ))
                          }
                        </select>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            ) : (
              <p style={{ color: '#718096', fontSize: '0.9rem', margin: '0.75rem 0' }}>
                This user has no leagues. Delete this account permanently?
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={() => {
                  setDeletingUser(null);
                  setDeleteAction('delete');
                  setReassignUserId('');
                }}
                style={{
                  padding: '0.6rem 1.25rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  background: '#fff',
                  color: '#4a5568',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleteUserLoading || (deleteAction === 'reassign' && !reassignUserId)}
                style={{
                  padding: '0.6rem 1.25rem',
                  border: 'none',
                  borderRadius: 8,
                  background: deleteUserLoading || (deleteAction === 'reassign' && !reassignUserId)
                    ? '#e2e8f0' : '#e53e3e',
                  color: '#fff',
                  cursor: deleteUserLoading || (deleteAction === 'reassign' && !reassignUserId) ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}
              >
                {deleteUserLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Comp Subscription Modal */}
      {/* ============================================ */}
      {compingUser && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: '1.5rem',
            maxWidth: 440,
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 0.5rem', color: '#333' }}>
              Comp Subscription: {compingUser.name}
            </h3>
            <p style={{ color: '#718096', fontSize: '0.85rem', margin: '0 0 1rem' }}>
              {compingUser.email}
            </p>
            <form onSubmit={handleCompUser}>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontWeight: 500, fontSize: '0.85rem', marginBottom: 4, color: '#4a5568' }}>Plan Tier</label>
                <select
                  value={compForm.plan}
                  onChange={(e) => setCompForm(f => ({ ...f, plan: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                >
                  <option value="free">Pickup (Free)</option>
                  <option value="starter">League</option>
                  <option value="pro">Commissioner</option>
                </select>
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontWeight: 500, fontSize: '0.85rem', marginBottom: 4, color: '#4a5568' }}>Comped Until</label>
                <input
                  type="date"
                  value={compForm.compedUntil}
                  onChange={(e) => setCompForm(f => ({ ...f, compedUntil: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
                <p style={{ margin: '4px 0 0', color: '#a0aec0', fontSize: '0.75rem' }}>
                  Leave blank for no expiration check (plan set manually).
                </p>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 500, fontSize: '0.85rem', marginBottom: 4, color: '#4a5568' }}>Reason</label>
                <input
                  type="text"
                  value={compForm.compReason}
                  onChange={(e) => setCompForm(f => ({ ...f, compReason: e.target.value }))}
                  placeholder="e.g. Beta participant, Early adopter"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {compingUser.compedUntil && (
                  <button
                    type="button"
                    onClick={() => { handleRemoveComp(compingUser.id); setCompingUser(null); }}
                    style={{
                      padding: '0.6rem 1rem',
                      border: '1px solid #e53e3e',
                      borderRadius: 8,
                      background: '#fff',
                      color: '#e53e3e',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      marginRight: 'auto'
                    }}
                  >
                    Remove Comp
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setCompingUser(null)}
                  style={{
                    padding: '0.6rem 1.25rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    background: '#fff',
                    color: '#4a5568',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={compLoading}
                  style={{
                    padding: '0.6rem 1.25rem',
                    border: 'none',
                    borderRadius: 8,
                    background: compLoading ? '#e2e8f0' : '#38a169',
                    color: '#fff',
                    cursor: compLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}
                >
                  {compLoading ? 'Saving...' : 'Apply Comp'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Leagues Tab */}
      {/* ============================================ */}
      {activeTab === 'leagues' && (
        <div>
          {/* Search & Sort */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search by league name, owner, city, state, sport..."
              value={leagueSearch}
              onChange={(e) => setLeagueSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: '0.6rem 1rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem' }}
            />
            <select
              value={leagueSort}
              onChange={(e) => setLeagueSort(e.target.value)}
              style={{ padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', background: '#fff' }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="most-teams">Most Teams</option>
            </select>
          </div>

          {leaguesLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>Loading leagues...</div>
          ) : (
            <>
              {leagueSearch && (
                <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '0.75rem' }}>
                  {getFilteredLeagues().length} of {adminLeagues?.length || 0} leagues
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {getFilteredLeagues().map(league => (
                  <div key={league.id} style={{
                    background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '1.25rem 1.5rem'
                  }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <h3 style={{ margin: 0, color: '#333', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {league.name}
                        {league.sport && league.sport !== 'general' && (
                          <span style={{
                            fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20,
                            background: 'rgba(102,126,234,0.1)', color: '#667eea', fontWeight: 600
                          }}>{league.sport}</span>
                        )}
                      </h3>
                      <span style={{
                        fontSize: '0.75rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                        background: league.status === 'published' ? '#c6f6d5' : league.status === 'completed' ? '#bee3f8' : '#edf2f7',
                        color: league.status === 'published' ? '#22543d' : league.status === 'completed' ? '#2a4365' : '#4a5568'
                      }}>{league.status}</span>
                    </div>

                    {/* Season & Location */}
                    <div style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                      {league.seasonYear}
                      {(league.city || league.state) && ` \u2022 ${[league.city, league.state].filter(Boolean).join(', ')}`}
                    </div>

                    {/* Owner */}
                    <div style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      Owner: <strong style={{ color: '#333' }}>{league.owner?.name || 'Unknown'}</strong> ({league.owner?.email || 'N/A'})
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase',
                        background: league.owner?.plan === 'pro' ? '#667eea' : league.owner?.plan === 'starter' ? '#764ba2' : '#e2e8f0',
                        color: league.owner?.plan === 'free' ? '#4a5568' : '#fff'
                      }}>{getPlanDisplayName(league.owner?.plan || 'free')}</span>
                    </div>

                    {/* Stats */}
                    <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '0.75rem', fontSize: '0.85rem', color: '#4a5568' }}>
                      <span>{league.teamCount} teams</span>
                      <span>{league.playedGameCount}/{league.gameCount} games played</span>
                      {league.isPublic && <span style={{ color: '#38a169', fontWeight: 600 }}>Public</span>}
                      {league.fairnessGrade && (
                        <span style={{
                          fontWeight: 600,
                          color: league.fairnessGrade === 'A' ? '#38a169' : league.fairnessGrade === 'B' ? '#d69e2e' : '#e53e3e'
                        }}>Grade: {league.fairnessGrade}</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleViewLeagueDetail(league)}
                        style={{
                          padding: '6px 14px', borderRadius: 6, border: '1px solid #667eea', background: 'transparent',
                          color: '#667eea', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'
                        }}
                      >View Details</button>
                      <button onClick={() => handleExportLeagueFromTab(league)} className="admin-action-btn admin-action-export">
                        Export
                      </button>
                      <button onClick={() => handleDeleteLeagueFromTab(league.id, league.name)} className="admin-action-btn admin-action-delete">
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {getFilteredLeagues().length === 0 && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>
                    {leagueSearch ? `No leagues matching "${leagueSearch}"` : 'No leagues found'}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* League Detail Modal */}
      {/* ============================================ */}
      {selectedLeague && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }} onClick={() => { setSelectedLeague(null); setLeagueDetail(null); setReassigningLeague(null); }}>
          <div
            style={{
              background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 640, width: '100%',
              maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {leagueDetailLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>Loading league details...</div>
            ) : leagueDetail ? (
              <>
                {/* Header */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>{leagueDetail.name}</h2>
                    <span style={{
                      fontSize: '0.75rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                      background: leagueDetail.status === 'published' ? '#c6f6d5' : leagueDetail.status === 'completed' ? '#bee3f8' : '#edf2f7',
                      color: leagueDetail.status === 'published' ? '#22543d' : leagueDetail.status === 'completed' ? '#2a4365' : '#4a5568'
                    }}>{leagueDetail.status}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#718096' }}>
                    <span>{leagueDetail.seasonYear}</span>
                    {leagueDetail.sport && leagueDetail.sport !== 'general' && <span style={{ color: '#667eea', fontWeight: 600 }}>{leagueDetail.sport}</span>}
                    {(leagueDetail.city || leagueDetail.state) && <span>{[leagueDetail.city, leagueDetail.state].filter(Boolean).join(', ')}</span>}
                    <span>Slug: /{leagueDetail.slug}</span>
                  </div>
                </div>

                {/* Owner Section */}
                <div style={{ background: '#f7fafc', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>
                        Owner: {selectedLeague.owner?.name || 'Unknown'}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#718096' }}>
                        {selectedLeague.owner?.email || 'N/A'}
                        <span style={{
                          marginLeft: 6, fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase',
                          background: selectedLeague.owner?.plan === 'pro' ? '#667eea' : selectedLeague.owner?.plan === 'starter' ? '#764ba2' : '#e2e8f0',
                          color: selectedLeague.owner?.plan === 'free' ? '#4a5568' : '#fff'
                        }}>{getPlanDisplayName(selectedLeague.owner?.plan || 'free')}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setReassigningLeague(reassigningLeague ? null : selectedLeague.id)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: '1px solid #d69e2e', background: 'transparent',
                        color: '#d69e2e', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer'
                      }}
                    >{reassigningLeague ? 'Cancel' : 'Reassign'}</button>
                  </div>

                  {/* Reassign Panel */}
                  {reassigningLeague === selectedLeague?.id && (
                    <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fffbeb', borderRadius: 8, border: '1px solid #fcd34d' }}>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>
                        Reassign to user:
                      </label>
                      <select
                        value={reassignLeagueUserId}
                        onChange={(e) => setReassignLeagueUserId(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.85rem', background: '#fff' }}
                      >
                        <option value="">Select a user...</option>
                        {data?.users
                          ?.filter(u => u.id !== leagueDetail?.userId)
                          .map(u => (
                            <option key={u.id} value={u.id}>{u.name} ({u.email}) - {u.plan}</option>
                          ))
                        }
                      </select>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button
                          onClick={handleReassignLeague}
                          disabled={!reassignLeagueUserId || reassignLeagueLoading}
                          style={{
                            padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                            background: !reassignLeagueUserId || reassignLeagueLoading ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: !reassignLeagueUserId || reassignLeagueLoading ? '#718096' : '#fff'
                          }}
                        >{reassignLeagueLoading ? 'Reassigning...' : 'Confirm Reassign'}</button>
                        <button
                          onClick={() => { setReassigningLeague(null); setReassignLeagueUserId(''); }}
                          style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#718096', fontSize: '0.8rem', cursor: 'pointer' }}
                        >Cancel</button>
                      </div>
                      <small style={{ color: '#92400e', fontSize: '0.75rem', marginTop: 4, display: 'block' }}>
                        Note: Game venue and referee assignments will remain but may not appear in the new owner's library.
                      </small>
                    </div>
                  )}
                </div>

                {/* Settings Row */}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.82rem' }}>
                  <span style={{ color: leagueDetail.isPublic ? '#38a169' : '#a0aec0' }}>
                    {leagueDetail.isPublic ? '\u2713 Public' : '\u2717 Private'}
                  </span>
                  <span style={{ color: leagueDetail.showStandings ? '#38a169' : '#a0aec0' }}>
                    {leagueDetail.showStandings ? '\u2713 Standings' : '\u2717 Standings'}
                  </span>
                  {leagueDetail.notifyOnScores && <span style={{ color: '#667eea' }}>\u2713 Score Emails</span>}
                  {leagueDetail.captainScoreEntry && <span style={{ color: '#667eea' }}>\u2713 Captain Scores</span>}
                  {leagueDetail.refereeScoreEntry && <span style={{ color: '#667eea' }}>\u2713 Referee Scores</span>}
                  {leagueDetail.playoffSettings?.enabled && <span style={{ color: '#667eea' }}>\u2713 Playoffs ({leagueDetail.playoffSettings.teamsQualifying} teams)</span>}
                </div>

                {/* Teams */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#4a5568' }}>
                    Teams ({leagueDetail.teams?.length || 0})
                  </h4>
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.82rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f7fafc', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#4a5568' }}>Team</th>
                          <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#4a5568' }}>Captain</th>
                          <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: '#4a5568' }}>Grp</th>
                          <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: '#4a5568' }}>Record</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(leagueDetail.teams || []).map((team, i) => (
                          <tr key={team.id} style={{ borderTop: '1px solid #edf2f7', background: i % 2 ? '#fafbfc' : '#fff' }}>
                            <td style={{ padding: '6px 10px' }}>{team.name}</td>
                            <td style={{ padding: '6px 10px', color: '#718096' }}>{team.captain?.name || team.captainName || '-'}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{team.groupNumber}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{team.wins}-{team.losses}-{team.ties}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Schedule Summary */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#4a5568' }}>Schedule</h4>
                  <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#4a5568' }}>
                    <span>{leagueDetail.games?.length || 0} total games</span>
                    <span>{(leagueDetail.games || []).filter(g => g.played).length} played</span>
                    {leagueDetail.scheduleData?.originalInput?.gameDates && (
                      <span>{leagueDetail.scheduleData.originalInput.gameDates.length} weeks</span>
                    )}
                    {leagueDetail.scheduleData?.originalInput?.timeSlots && (
                      <span>{leagueDetail.scheduleData.originalInput.timeSlots.length} time slots</span>
                    )}
                  </div>
                </div>

                {/* Fairness Grade */}
                {leagueDetail.scheduleData?.fairnessAnalysis && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#4a5568', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      Fairness
                      <span style={{
                        fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                        background: leagueDetail.scheduleData.fairnessAnalysis.fairnessGrade === 'A' ? '#c6f6d5' :
                          leagueDetail.scheduleData.fairnessAnalysis.fairnessGrade === 'B' ? '#fefcbf' : '#fed7d7',
                        color: leagueDetail.scheduleData.fairnessAnalysis.fairnessGrade === 'A' ? '#22543d' :
                          leagueDetail.scheduleData.fairnessAnalysis.fairnessGrade === 'B' ? '#744210' : '#9b2c2c'
                      }}>Grade: {leagueDetail.scheduleData.fairnessAnalysis.fairnessGrade}</span>
                    </h4>
                    {leagueDetail.scheduleData.fairnessAnalysis.summary && (
                      <p style={{ margin: 0, fontSize: '0.82rem', color: '#718096' }}>
                        {leagueDetail.scheduleData.fairnessAnalysis.summary}
                      </p>
                    )}
                  </div>
                )}

                {/* Created date */}
                <div style={{ fontSize: '0.8rem', color: '#a0aec0', marginBottom: '1rem' }}>
                  Created {formatDate(leagueDetail.createdAt)}
                </div>
              </>
            ) : null}

            {/* Close Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button
                onClick={() => { setSelectedLeague(null); setLeagueDetail(null); setReassigningLeague(null); }}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff',
                  color: '#4a5568', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 500
                }}
              >Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <AnalyticsTab
          analytics={analytics}
          loading={analyticsLoading}
          error={analyticsError}
        />
      )}

      {/* ============================================ */}
      {/* Migrations Tab */}
      {/* ============================================ */}
      {activeTab === 'migrations' && (
        <div>
          {/* Sub-tab bar */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {['account', 'league', 'history'].map(sub => (
              <button
                key={sub}
                onClick={() => setMigrationSubTab(sub)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: migrationSubTab === sub ? '2px solid #667eea' : '1px solid #e2e8f0',
                  background: migrationSubTab === sub ? '#667eea' : '#fff',
                  color: migrationSubTab === sub ? '#fff' : '#4a5568',
                  fontWeight: migrationSubTab === sub ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {sub === 'account' ? 'Transfer Account' : sub === 'league' ? 'Transfer League' : 'Migration History'}
              </button>
            ))}
          </div>

          {/* Transfer Account Sub-tab */}
          {migrationSubTab === 'account' && (
            <div style={{ maxWidth: 600 }}>
              <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Move all leagues, captains, venues, and referees from one user to another.
              </p>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>Source User</label>
                  <select
                    value={acctSourceUserId}
                    onChange={(e) => { setAcctSourceUserId(e.target.value); setAcctPreview(null); }}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.9rem' }}
                  >
                    <option value="">Select source user...</option>
                    {data?.users?.filter(u => u.id !== acctTargetUserId).map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>Target User</label>
                  <select
                    value={acctTargetUserId}
                    onChange={(e) => { setAcctTargetUserId(e.target.value); setAcctPreview(null); }}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.9rem' }}
                  >
                    <option value="">Select target user...</option>
                    {data?.users?.filter(u => u.id !== acctSourceUserId).map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email}) - {u.plan}</option>
                    ))}
                  </select>
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#4a5568' }}>
                <input
                  type="checkbox"
                  checked={acctDeleteSource}
                  onChange={(e) => { setAcctDeleteSource(e.target.checked); setAcctPreview(null); }}
                />
                Delete source user after migration
              </label>

              <button
                onClick={handlePreviewAccountMigration}
                disabled={!acctSourceUserId || !acctTargetUserId || acctPreviewLoading}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: '6px',
                  border: '1px solid #667eea',
                  background: (!acctSourceUserId || !acctTargetUserId) ? '#e2e8f0' : '#fff',
                  color: (!acctSourceUserId || !acctTargetUserId) ? '#a0aec0' : '#667eea',
                  fontWeight: 600,
                  cursor: (!acctSourceUserId || !acctTargetUserId) ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  marginBottom: '1rem'
                }}
              >
                {acctPreviewLoading ? 'Loading...' : 'Preview Migration'}
              </button>

              {/* Account Preview Panel */}
              {acctPreview && (
                <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#2d3748' }}>Migration Preview</h4>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{ background: '#fff', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 600, marginBottom: '0.25rem' }}>FROM</div>
                      <div style={{ fontWeight: 600, color: '#2d3748' }}>{acctPreview.source.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#718096' }}>{acctPreview.source.email}</div>
                      <span style={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', background: '#edf2f7', color: '#4a5568' }}>{getPlanDisplayName(acctPreview.source.plan)}</span>
                    </div>
                    <div style={{ background: '#fff', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 600, marginBottom: '0.25rem' }}>TO</div>
                      <div style={{ fontWeight: 600, color: '#2d3748' }}>{acctPreview.target.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#718096' }}>{acctPreview.target.email}</div>
                      <span style={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', background: '#edf2f7', color: '#4a5568' }}>{getPlanDisplayName(acctPreview.target.plan)}</span>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {[
                      { label: 'Leagues', count: acctPreview.leagues.length },
                      { label: 'Captains', count: acctPreview.captains.total },
                      { label: 'Venues', count: acctPreview.venues.total },
                      { label: 'Referees', count: acctPreview.referees.total }
                    ].map(item => (
                      <div key={item.label} style={{ textAlign: 'center', background: '#fff', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#667eea' }}>{item.count}</div>
                        <div style={{ fontSize: '0.75rem', color: '#718096' }}>{item.label}</div>
                      </div>
                    ))}
                  </div>

                  {acctPreview.leagues.length > 0 && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>Leagues:</div>
                      {acctPreview.leagues.map(l => (
                        <div key={l.id} style={{ fontSize: '0.85rem', color: '#718096', paddingLeft: '0.5rem' }}>
                          {l.name} ({l.sport}, {l.teamCount} teams, {l.gameCount} games)
                        </div>
                      ))}
                    </div>
                  )}

                  {acctPreview.warnings.length > 0 && (
                    <div style={{ background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: '6px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                      {acctPreview.warnings.map((w, i) => (
                        <div key={i} style={{ fontSize: '0.85rem', color: '#744210' }}>{w}</div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={handleExecuteAccountMigration}
                    disabled={acctMigrating}
                    style={{
                      padding: '0.5rem 1.25rem',
                      borderRadius: '6px',
                      border: 'none',
                      background: acctMigrating ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: acctMigrating ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {acctMigrating ? 'Migrating...' : 'Confirm Migration'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Transfer League Sub-tab */}
          {migrationSubTab === 'league' && (
            <div style={{ maxWidth: 600 }}>
              <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Transfer a single league to another user. Captains, venues, and referees will be copied (not moved).
              </p>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>League</label>
                <select
                  value={leagueMigLeagueId}
                  onChange={(e) => { setLeagueMigLeagueId(e.target.value); setLeagueMigPreview(null); }}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.9rem' }}
                >
                  <option value="">Select a league...</option>
                  {adminLeagues?.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.name} — {l.owner?.name || 'Unknown'} ({l.sport}, {l.teamCount} teams)
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>Target User</label>
                <select
                  value={leagueMigTargetUserId}
                  onChange={(e) => { setLeagueMigTargetUserId(e.target.value); setLeagueMigPreview(null); }}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.9rem' }}
                >
                  <option value="">Select target user...</option>
                  {data?.users?.filter(u => {
                    if (!leagueMigLeagueId) return true;
                    const league = adminLeagues?.find(l => l.id === leagueMigLeagueId);
                    return league ? u.id !== league.userId : true;
                  }).map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email}) - {u.plan}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={handlePreviewLeagueMigration}
                disabled={!leagueMigLeagueId || !leagueMigTargetUserId || leagueMigPreviewLoading}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: '6px',
                  border: '1px solid #667eea',
                  background: (!leagueMigLeagueId || !leagueMigTargetUserId) ? '#e2e8f0' : '#fff',
                  color: (!leagueMigLeagueId || !leagueMigTargetUserId) ? '#a0aec0' : '#667eea',
                  fontWeight: 600,
                  cursor: (!leagueMigLeagueId || !leagueMigTargetUserId) ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  marginBottom: '1rem'
                }}
              >
                {leagueMigPreviewLoading ? 'Loading...' : 'Preview Migration'}
              </button>

              {/* League Preview Panel */}
              {leagueMigPreview && (
                <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#2d3748' }}>Migration Preview</h4>

                  <div style={{ background: '#fff', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '0.75rem' }}>
                    <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: '0.25rem' }}>{leagueMigPreview.league.name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                      {leagueMigPreview.league.sport} — {leagueMigPreview.league.teamCount} teams, {leagueMigPreview.league.gameCount} games, {leagueMigPreview.league.seasonCount} archived season(s)
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.25rem' }}>
                      From: <strong>{leagueMigPreview.currentOwner?.name}</strong> → To: <strong>{leagueMigPreview.targetUser.name}</strong> ({leagueMigPreview.targetUser.plan})
                    </div>
                  </div>

                  {/* Entity details */}
                  {[
                    { label: 'Captains', data: leagueMigPreview.captains },
                    { label: 'Venues', data: leagueMigPreview.venues },
                    { label: 'Referees', data: leagueMigPreview.referees }
                  ].map(section => (
                    section.data.total > 0 && (
                      <div key={section.label} style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>
                          {section.label}: {section.data.toCreate} to copy, {section.data.toDedup} to reuse
                        </div>
                        {section.data.details.map((item, i) => (
                          <div key={i} style={{ fontSize: '0.85rem', paddingLeft: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{
                              fontSize: '0.7rem',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              background: item.action === 'create' ? '#ebf4ff' : '#f0fff4',
                              color: item.action === 'create' ? '#3182ce' : '#38a169',
                              fontWeight: 600
                            }}>
                              {item.action === 'create' ? 'NEW' : 'REUSE'}
                            </span>
                            <span style={{ color: '#4a5568' }}>{item.name}</span>
                            {item.email && <span style={{ color: '#a0aec0', fontSize: '0.8rem' }}>({item.email})</span>}
                          </div>
                        ))}
                      </div>
                    )
                  ))}

                  {leagueMigPreview.warnings.length > 0 && (
                    <div style={{ background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: '6px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                      {leagueMigPreview.warnings.map((w, i) => (
                        <div key={i} style={{ fontSize: '0.85rem', color: '#744210' }}>{w}</div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={handleExecuteLeagueMigration}
                    disabled={leagueMigrating}
                    style={{
                      padding: '0.5rem 1.25rem',
                      borderRadius: '6px',
                      border: 'none',
                      background: leagueMigrating ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: leagueMigrating ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {leagueMigrating ? 'Migrating...' : 'Confirm Migration'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Migration History Sub-tab */}
          {migrationSubTab === 'history' && (
            <div>
              {migrationLogsLoading && <p style={{ color: '#718096' }}>Loading migration logs...</p>}
              {!migrationLogsLoading && migrationLogs && migrationLogs.length === 0 && (
                <p style={{ color: '#718096' }}>No migrations have been performed yet.</p>
              )}
              {migrationLogs?.map(log => (
                <div key={log.id} style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '0.7rem',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: log.type === 'account' ? '#ebf4ff' : '#faf5ff',
                      color: log.type === 'account' ? '#3182ce' : '#805ad5',
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}>
                      {log.type}
                    </span>
                    {log.leagueName && (
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#2d3748' }}>"{log.leagueName}"</span>
                    )}
                    <span style={{ fontSize: '0.8rem', color: '#a0aec0', marginLeft: 'auto' }}>
                      {new Date(log.createdAt).toLocaleDateString()} {new Date(log.createdAt).toLocaleTimeString()}
                    </span>
                  </div>

                  <div style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.25rem' }}>
                    <strong>{log.sourceUserName}</strong> ({log.sourceUserEmail})
                    <span style={{ margin: '0 0.5rem', color: '#a0aec0' }}>&rarr;</span>
                    <strong>{log.targetUserName}</strong> ({log.targetUserEmail})
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#718096', marginTop: '0.25rem' }}>
                    {log.type === 'account' ? (
                      <>
                        {log.details.leaguesMigrated > 0 && <span>{log.details.leaguesMigrated} league(s)</span>}
                        {log.details.captainsMoved > 0 && <span>{log.details.captainsMoved} captain(s)</span>}
                        {log.details.venuesMoved > 0 && <span>{log.details.venuesMoved} venue(s)</span>}
                        {log.details.refereesMoved > 0 && <span>{log.details.refereesMoved} referee(s)</span>}
                      </>
                    ) : (
                      <>
                        {log.details.teamCount > 0 && <span>{log.details.teamCount} teams</span>}
                        {log.details.gameCount > 0 && <span>{log.details.gameCount} games</span>}
                        {(log.details.captainsCopied > 0 || log.details.captainsDeduped > 0) && (
                          <span>{log.details.captainsCopied} captain(s) copied, {log.details.captainsDeduped} reused</span>
                        )}
                        {(log.details.venuesCopied > 0 || log.details.venuesDeduped > 0) && (
                          <span>{log.details.venuesCopied} venue(s) copied, {log.details.venuesDeduped} reused</span>
                        )}
                        {(log.details.refereesCopied > 0 || log.details.refereesDeduped > 0) && (
                          <span>{log.details.refereesCopied} referee(s) copied, {log.details.refereesDeduped} reused</span>
                        )}
                      </>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem', fontSize: '0.8rem' }}>
                    {log.sourceUserDeleted && (
                      <span style={{ color: '#e53e3e', fontWeight: 600 }}>Source user deleted</span>
                    )}
                    <span style={{ color: '#a0aec0' }}>by {log.adminUserName}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Import League Tab */}
      {activeTab === 'import' && (
        <div style={{ maxWidth: 540 }}>
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={handleDownloadTemplate}
              style={{
                background: 'none',
                border: 'none',
                color: '#667eea',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.9rem',
                padding: 0
              }}
            >
              Download template JSON
            </button>
          </div>

          {/* Target user selector for admin import */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.25rem' }}>
              Import for user
            </label>
            <select
              value={importTargetUserId}
              onChange={(e) => setImportTargetUserId(e.target.value)}
              style={{
                width: '100%', maxWidth: 400, padding: '0.5rem 0.75rem',
                border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', background: '#fff'
              }}
            >
              <option value="">Myself (default)</option>
              {data?.users?.map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.email}) - {u.plan}</option>
              ))}
            </select>
            <small style={{ color: '#718096', fontSize: '0.78rem', display: 'block', marginTop: 4 }}>
              The league will be created under this user's account.
            </small>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setImportDragOver(true); }}
            onDragLeave={() => setImportDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setImportDragOver(false);
              const file = e.dataTransfer.files[0];
              if (file) handleImportFileSelect(file);
            }}
            style={{
              border: `2px dashed ${importDragOver ? '#667eea' : '#e2e8f0'}`,
              borderRadius: 8,
              padding: '24px 16px',
              textAlign: 'center',
              background: importDragOver ? '#f0f4ff' : '#f7fafc',
              marginBottom: 16,
              transition: 'all 0.2s'
            }}
          >
            {importFile ? (
              <div>
                <span style={{ fontSize: '0.9rem', color: '#333' }}>
                  {importFileName}
                </span>
                <button
                  onClick={() => { setImportFile(null); setImportFileName(''); setImportErrors(null); }}
                  style={{
                    marginLeft: 12,
                    background: 'none',
                    border: 'none',
                    color: '#e53e3e',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    textDecoration: 'underline'
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <p style={{ margin: '0 0 8px', color: '#718096', fontSize: '0.9rem' }}>
                  Drag & drop a .json file here, or
                </p>
                <label
                  style={{
                    display: 'inline-block',
                    padding: '6px 16px',
                    background: '#667eea',
                    color: '#fff',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  Choose File
                  <input
                    type="file"
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      handleImportFileSelect(e.target.files[0]);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Preview */}
          {importFile && !importErrors && (
            <div style={{
              background: '#f0f4ff',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              fontSize: '0.85rem'
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#333' }}>Preview</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                <span style={{ color: '#718096' }}>League:</span>
                <span>{importFile.leagueName || '—'}</span>
                <span style={{ color: '#718096' }}>Season:</span>
                <span>{importFile.season || '—'}</span>
                <span style={{ color: '#718096' }}>Sport:</span>
                <span>{importFile.sport || 'general'}</span>
                <span style={{ color: '#718096' }}>Teams:</span>
                <span>{Array.isArray(importFile.teams) ? importFile.teams.length : 0}</span>
                <span style={{ color: '#718096' }}>Game Dates:</span>
                <span>{Array.isArray(importFile.gameDates) ? importFile.gameDates.length : 0}</span>
                <span style={{ color: '#718096' }}>Time Slots:</span>
                <span>{Array.isArray(importFile.timeSlots) ? importFile.timeSlots.length : 0}</span>
                {importFile.venues && (
                  <>
                    <span style={{ color: '#718096' }}>Venues:</span>
                    <span>{Array.isArray(importFile.venues) ? importFile.venues.length : 0}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {importErrors && (
            <div style={{
              background: '#fff5f5',
              border: '1px solid #fecaca',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              maxHeight: 200,
              overflowY: 'auto'
            }}>
              <div style={{ fontWeight: 600, color: '#e53e3e', marginBottom: 6, fontSize: '0.85rem' }}>
                Validation Errors ({importErrors.length})
              </div>
              {importErrors.map((err, i) => (
                <div key={i} style={{ fontSize: '0.82rem', marginBottom: 4, color: '#333' }}>
                  <span style={{ color: '#e53e3e', fontWeight: 500 }}>{err.field}</span>
                  {': '}
                  {err.message}
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleImportSubmit}
            disabled={!importFile || importingLeague}
            style={{
              padding: '10px 24px',
              background: !importFile || importingLeague ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: !importFile || importingLeague ? 'not-allowed' : 'pointer'
            }}
          >
            {importingLeague ? 'Importing...' : 'Import League'}
          </button>
        </div>
      )}

      {/* ============================================ */}
      {/* Sports Tab */}
      {/* ============================================ */}
      {activeTab === 'sports' && (
        <div>
          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '1rem', color: 'var(--color-text-secondary, #718096)' }}>
              {sportsList ? `${sportsList.length} sport${sportsList.length !== 1 ? 's' : ''}` : ''}
            </div>
            <button
              onClick={() => {
                setEditingSport(null);
                setSportForm({
                  key: '', name: '', scoreLabel: 'Score', officialTitle: 'Referee',
                  pointsForWin: 2, pointsForTie: 1, pointsForLoss: 0, allowTies: true,
                  winLossOnly: false, supportsBestOf: false, supportsPlayerRatings: false,
                  statLabelGF: 'PF', statLabelGA: 'PA', statLabelGD: 'Diff',
                  sortOrder: 999, isActive: true
                });
                setShowSportForm(true);
              }}
              style={{
                padding: '0.5rem 1.25rem', background: '#667eea', color: 'white',
                border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem'
              }}
            >
              + Add Custom Sport
            </button>
          </div>

          {sportsError && <div style={{ color: '#e53e3e', marginBottom: '1rem' }}>{sportsError}</div>}

          {sportsLoading && <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary, #718096)' }}>Loading sports...</div>}

          {/* Sport Form Modal */}
          {showSportForm && (
            <div style={{
              background: 'var(--color-card, white)', border: '1px solid var(--color-border, #e2e8f0)',
              borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 1rem', color: 'var(--color-text, #333)' }}>
                {editingSport ? 'Edit Sport' : 'Add Custom Sport'}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {!editingSport && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-secondary, #4a5568)', marginBottom: '0.25rem' }}>Key (unique ID)</label>
                    <input
                      type="text"
                      value={sportForm.key}
                      onChange={e => setSportForm({ ...sportForm, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                      placeholder="e.g. rugby"
                      style={{
                        width: '100%', padding: '0.5rem', borderRadius: '6px',
                        border: '1px solid var(--color-border, #cbd5e0)',
                        background: 'var(--color-bg-secondary, #f7fafc)',
                        color: 'var(--color-text, #333)', fontSize: '0.9rem', boxSizing: 'border-box'
                      }}
                    />
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-secondary, #4a5568)', marginBottom: '0.25rem' }}>Display Name</label>
                  <input
                    type="text"
                    value={sportForm.name}
                    onChange={e => setSportForm({ ...sportForm, name: e.target.value })}
                    placeholder="e.g. Rugby"
                    style={{
                      width: '100%', padding: '0.5rem', borderRadius: '6px',
                      border: '1px solid var(--color-border, #cbd5e0)',
                      background: 'var(--color-bg-secondary, #f7fafc)',
                      color: 'var(--color-text, #333)', fontSize: '0.9rem', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-secondary, #4a5568)', marginBottom: '0.25rem' }}>Score Label</label>
                  <input
                    type="text"
                    value={sportForm.scoreLabel}
                    onChange={e => setSportForm({ ...sportForm, scoreLabel: e.target.value })}
                    placeholder="e.g. Goals, Points, Runs"
                    style={{
                      width: '100%', padding: '0.5rem', borderRadius: '6px',
                      border: '1px solid var(--color-border, #cbd5e0)',
                      background: 'var(--color-bg-secondary, #f7fafc)',
                      color: 'var(--color-text, #333)', fontSize: '0.9rem', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-secondary, #4a5568)', marginBottom: '0.25rem' }}>Official Title</label>
                  <input
                    type="text"
                    value={sportForm.officialTitle}
                    onChange={e => setSportForm({ ...sportForm, officialTitle: e.target.value })}
                    placeholder="e.g. Referee, Umpire"
                    style={{
                      width: '100%', padding: '0.5rem', borderRadius: '6px',
                      border: '1px solid var(--color-border, #cbd5e0)',
                      background: 'var(--color-bg-secondary, #f7fafc)',
                      color: 'var(--color-text, #333)', fontSize: '0.9rem', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-secondary, #4a5568)', marginBottom: '0.25rem' }}>Points for Win</label>
                  <input
                    type="number" min="0" max="10"
                    value={sportForm.pointsForWin}
                    onChange={e => setSportForm({ ...sportForm, pointsForWin: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%', padding: '0.5rem', borderRadius: '6px',
                      border: '1px solid var(--color-border, #cbd5e0)',
                      background: 'var(--color-bg-secondary, #f7fafc)',
                      color: 'var(--color-text, #333)', fontSize: '0.9rem', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-secondary, #4a5568)', marginBottom: '0.25rem' }}>Points for Tie</label>
                  <input
                    type="number" min="0" max="10"
                    value={sportForm.pointsForTie}
                    onChange={e => setSportForm({ ...sportForm, pointsForTie: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%', padding: '0.5rem', borderRadius: '6px',
                      border: '1px solid var(--color-border, #cbd5e0)',
                      background: 'var(--color-bg-secondary, #f7fafc)',
                      color: 'var(--color-text, #333)', fontSize: '0.9rem', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-secondary, #4a5568)', marginBottom: '0.25rem' }}>Points for Loss</label>
                  <input
                    type="number" min="0" max="10"
                    value={sportForm.pointsForLoss}
                    onChange={e => setSportForm({ ...sportForm, pointsForLoss: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%', padding: '0.5rem', borderRadius: '6px',
                      border: '1px solid var(--color-border, #cbd5e0)',
                      background: 'var(--color-bg-secondary, #f7fafc)',
                      color: 'var(--color-text, #333)', fontSize: '0.9rem', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-secondary, #4a5568)', marginBottom: '0.25rem' }}>Sort Order</label>
                  <input
                    type="number" min="0"
                    value={sportForm.sortOrder}
                    onChange={e => setSportForm({ ...sportForm, sortOrder: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%', padding: '0.5rem', borderRadius: '6px',
                      border: '1px solid var(--color-border, #cbd5e0)',
                      background: 'var(--color-bg-secondary, #f7fafc)',
                      color: 'var(--color-text, #333)', fontSize: '0.9rem', boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Stat Labels */}
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-secondary, #4a5568)', marginBottom: '0.5rem' }}>Stat Column Labels</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary, #718096)' }}>Points/Goals For</label>
                    <input
                      type="text" value={sportForm.statLabelGF}
                      onChange={e => setSportForm({ ...sportForm, statLabelGF: e.target.value })}
                      placeholder="PF"
                      disabled={sportForm.winLossOnly}
                      style={{
                        width: '100%', padding: '0.4rem', borderRadius: '6px',
                        border: '1px solid var(--color-border, #cbd5e0)',
                        background: sportForm.winLossOnly ? 'var(--color-bg-disabled, #edf2f7)' : 'var(--color-bg-secondary, #f7fafc)',
                        color: 'var(--color-text, #333)', fontSize: '0.85rem', boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary, #718096)' }}>Points/Goals Against</label>
                    <input
                      type="text" value={sportForm.statLabelGA}
                      onChange={e => setSportForm({ ...sportForm, statLabelGA: e.target.value })}
                      placeholder="PA"
                      disabled={sportForm.winLossOnly}
                      style={{
                        width: '100%', padding: '0.4rem', borderRadius: '6px',
                        border: '1px solid var(--color-border, #cbd5e0)',
                        background: sportForm.winLossOnly ? 'var(--color-bg-disabled, #edf2f7)' : 'var(--color-bg-secondary, #f7fafc)',
                        color: 'var(--color-text, #333)', fontSize: '0.85rem', boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary, #718096)' }}>Differential</label>
                    <input
                      type="text" value={sportForm.statLabelGD}
                      onChange={e => setSportForm({ ...sportForm, statLabelGD: e.target.value })}
                      placeholder="Diff"
                      disabled={sportForm.winLossOnly}
                      style={{
                        width: '100%', padding: '0.4rem', borderRadius: '6px',
                        border: '1px solid var(--color-border, #cbd5e0)',
                        background: sportForm.winLossOnly ? 'var(--color-bg-disabled, #edf2f7)' : 'var(--color-bg-secondary, #f7fafc)',
                        color: 'var(--color-text, #333)', fontSize: '0.85rem', boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Toggles */}
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: 'var(--color-text, #4a5568)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={sportForm.allowTies} onChange={e => setSportForm({ ...sportForm, allowTies: e.target.checked })} />
                  Allow Ties
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: 'var(--color-text, #4a5568)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={sportForm.winLossOnly} onChange={e => setSportForm({ ...sportForm, winLossOnly: e.target.checked })} />
                  Win/Loss Only (no scores)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: 'var(--color-text, #4a5568)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={sportForm.supportsBestOf} onChange={e => setSportForm({ ...sportForm, supportsBestOf: e.target.checked })} />
                  Best-of Series
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: 'var(--color-text, #4a5568)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={sportForm.isActive} onChange={e => setSportForm({ ...sportForm, isActive: e.target.checked })} />
                  Active
                </label>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                <button
                  disabled={sportSaving}
                  onClick={async () => {
                    setSportSaving(true);
                    try {
                      const stats = sportForm.winLossOnly ? [] : ['goalsFor', 'goalsAgainst', 'goalDifferential'];
                      const statLabels = sportForm.winLossOnly ? {} : {
                        goalsFor: sportForm.statLabelGF || 'PF',
                        goalsAgainst: sportForm.statLabelGA || 'PA',
                        goalDifferential: sportForm.statLabelGD || 'Diff'
                      };
                      const tiebreakers = sportForm.winLossOnly
                        ? ['points', 'wins']
                        : (sportForm.pointsForWin > 1 ? ['points', 'goalDifferential', 'goalsFor'] : ['wins', 'goalDifferential', 'goalsFor']);

                      const defaultScoring = {
                        scoreLabel: sportForm.scoreLabel,
                        officialTitle: sportForm.officialTitle,
                        pointsForWin: sportForm.pointsForWin,
                        pointsForTie: sportForm.pointsForTie,
                        pointsForLoss: sportForm.pointsForLoss,
                        allowTies: sportForm.allowTies,
                        stats,
                        statLabels,
                        tiebreakers,
                        ...(sportForm.winLossOnly ? { winLossOnly: true } : {}),
                        ...(sportForm.supportsBestOf ? { supportsBestOf: true } : {}),
                        ...(sportForm.supportsPlayerRatings ? { supportsPlayerRatings: true } : {})
                      };

                      const scoreFields = [{ key: 'score', label: sportForm.scoreLabel, type: 'number', defaultValue: 0, required: true }];
                      const standingsColumns = stats.map(s => ({ key: s, label: statLabels[s] || s, sortable: true }));

                      if (editingSport) {
                        await api.updateAdminSport(editingSport.id, {
                          name: sportForm.name,
                          scoreFields,
                          standingsColumns,
                          defaultScoring,
                          sortOrder: sportForm.sortOrder,
                          isActive: sportForm.isActive
                        });
                        setToast({ type: 'success', message: `"${sportForm.name}" updated` });
                      } else {
                        if (!sportForm.key) {
                          setToast({ type: 'error', message: 'Sport key is required' });
                          setSportSaving(false);
                          return;
                        }
                        await api.createAdminSport({
                          key: sportForm.key,
                          name: sportForm.name,
                          scoreFields,
                          standingsColumns,
                          defaultScoring,
                          sortOrder: sportForm.sortOrder,
                          isActive: sportForm.isActive
                        });
                        setToast({ type: 'success', message: `"${sportForm.name}" created` });
                      }
                      setShowSportForm(false);
                      setEditingSport(null);
                      loadSports();
                      // Invalidate client sport config cache so dropdown picks up changes
                      try {
                        const { invalidateSportConfigCache, loadSportConfigsFromAPI } = require('../utils/sportConfig');
                        invalidateSportConfigCache();
                        loadSportConfigsFromAPI();
                      } catch (e) { /* ignore */ }
                    } catch (err) {
                      setToast({ type: 'error', message: err.message });
                    } finally {
                      setSportSaving(false);
                    }
                  }}
                  style={{
                    padding: '0.5rem 1.5rem', background: '#667eea', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: sportSaving ? 'wait' : 'pointer',
                    fontWeight: 600, fontSize: '0.9rem', opacity: sportSaving ? 0.7 : 1
                  }}
                >
                  {sportSaving ? 'Saving...' : editingSport ? 'Update Sport' : 'Create Sport'}
                </button>
                <button
                  onClick={() => { setShowSportForm(false); setEditingSport(null); }}
                  style={{
                    padding: '0.5rem 1.5rem', background: 'transparent',
                    color: 'var(--color-text-secondary, #718096)',
                    border: '1px solid var(--color-border, #cbd5e0)', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: 500, fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Sports List */}
          {sportsList && !sportsLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {sportsList.map(sport => (
                <div key={sport.id} style={{
                  background: 'var(--color-card, white)', border: '1px solid var(--color-border, #e2e8f0)',
                  borderRadius: '10px', padding: '1rem 1.25rem',
                  opacity: sport.isActive ? 1 : 0.6,
                  transition: 'opacity 0.2s'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <strong style={{ fontSize: '1.05rem', color: 'var(--color-text, #333)' }}>{sport.name}</strong>
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary, #718096)', background: 'var(--color-bg-secondary, #f7fafc)', padding: '0.15rem 0.5rem', borderRadius: '4px', fontFamily: 'monospace' }}>{sport.key}</span>
                      {sport.isBuiltIn && (
                        <span style={{ fontSize: '0.75rem', color: '#667eea', background: '#ebf4ff', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 600 }}>Built-in</span>
                      )}
                      {!sport.isActive && (
                        <span style={{ fontSize: '0.75rem', color: '#e53e3e', background: '#fff5f5', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 600 }}>Inactive</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary, #a0aec0)' }}>#{sport.sortOrder}</span>
                      <button
                        onClick={() => {
                          const ds = sport.defaultScoring || {};
                          setEditingSport(sport);
                          setSportForm({
                            key: sport.key,
                            name: sport.name,
                            scoreLabel: ds.scoreLabel || 'Score',
                            officialTitle: ds.officialTitle || 'Referee',
                            pointsForWin: ds.pointsForWin ?? 2,
                            pointsForTie: ds.pointsForTie ?? 1,
                            pointsForLoss: ds.pointsForLoss ?? 0,
                            allowTies: ds.allowTies !== false,
                            winLossOnly: !!ds.winLossOnly,
                            supportsBestOf: !!ds.supportsBestOf,
                            supportsPlayerRatings: !!ds.supportsPlayerRatings,
                            statLabelGF: (ds.statLabels || {}).goalsFor || 'PF',
                            statLabelGA: (ds.statLabels || {}).goalsAgainst || 'PA',
                            statLabelGD: (ds.statLabels || {}).goalDifferential || 'Diff',
                            sortOrder: sport.sortOrder ?? 0,
                            isActive: sport.isActive !== false
                          });
                          setShowSportForm(true);
                        }}
                        style={{
                          padding: '0.3rem 0.75rem', background: 'transparent',
                          color: '#667eea', border: '1px solid #667eea', borderRadius: '6px',
                          cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500
                        }}
                      >
                        Edit
                      </button>
                      {!sport.isBuiltIn && (
                        sportDeleteId === sport.id ? (
                          <div style={{ display: 'flex', gap: '0.35rem' }}>
                            <button
                              onClick={async () => {
                                try {
                                  await api.deleteAdminSport(sport.id);
                                  setToast({ type: 'success', message: `"${sport.name}" deleted` });
                                  setSportDeleteId(null);
                                  loadSports();
                                } catch (err) {
                                  setToast({ type: 'error', message: err.message });
                                }
                              }}
                              style={{
                                padding: '0.3rem 0.6rem', background: '#e53e3e', color: 'white',
                                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem'
                              }}
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setSportDeleteId(null)}
                              style={{
                                padding: '0.3rem 0.6rem', background: 'transparent',
                                color: 'var(--color-text-secondary, #718096)',
                                border: '1px solid var(--color-border, #cbd5e0)', borderRadius: '6px',
                                cursor: 'pointer', fontSize: '0.8rem'
                              }}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setSportDeleteId(sport.id)}
                            style={{
                              padding: '0.3rem 0.75rem', background: 'transparent',
                              color: '#e53e3e', border: '1px solid #e53e3e', borderRadius: '6px',
                              cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500
                            }}
                          >
                            Delete
                          </button>
                        )
                      )}
                    </div>
                  </div>
                  {/* Sport details row */}
                  <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary, #718096)' }}>
                    <span>Score: <strong>{(sport.defaultScoring || {}).scoreLabel || 'Score'}</strong></span>
                    <span>W/T/L: <strong>{(sport.defaultScoring || {}).pointsForWin || 0}/{(sport.defaultScoring || {}).pointsForTie || 0}/{(sport.defaultScoring || {}).pointsForLoss || 0}</strong></span>
                    <span>Ties: <strong>{(sport.defaultScoring || {}).allowTies ? 'Yes' : 'No'}</strong></span>
                    {(sport.defaultScoring || {}).winLossOnly && <span style={{ fontStyle: 'italic' }}>Win/Loss Only</span>}
                    {(sport.defaultScoring || {}).supportsBestOf && <span style={{ fontStyle: 'italic' }}>Best-of</span>}
                    <span>Official: <strong>{(sport.defaultScoring || {}).officialTitle || 'Referee'}</strong></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* Blog Tab */}
      {/* ============================================ */}
      {activeTab === 'blog' && (
        <div>
          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '1rem', color: '#718096' }}>
              {blogArticles ? `${blogArticles.length} article${blogArticles.length !== 1 ? 's' : ''}` : ''}
            </div>
            {!showBlogForm && (
              <button
                onClick={() => {
                  setEditingArticle(null);
                  setBlogForm({ title: '', slug: '', excerpt: '', content: '', published: false });
                  setShowBlogForm(true);
                }}
                style={{
                  padding: '0.5rem 1.25rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                + New Article
              </button>
            )}
          </div>

          {/* Article form (create or edit) */}
          {showBlogForm && (
            <div style={{
              background: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ margin: '0 0 1.25rem', fontSize: '1.05rem', color: '#2d3748' }}>
                {editingArticle ? 'Edit Article' : 'New Article'}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#4a5568', marginBottom: 4 }}>Title *</label>
                  <input
                    value={blogForm.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      const autoSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                      setBlogForm(f => ({
                        ...f,
                        title,
                        slug: editingArticle ? f.slug : autoSlug
                      }));
                    }}
                    placeholder="How to Run a Great Soccer League"
                    style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#4a5568', marginBottom: 4 }}>Slug *</label>
                  <input
                    value={blogForm.slug}
                    onChange={(e) => setBlogForm(f => ({ ...f, slug: e.target.value }))}
                    placeholder="how-to-run-a-great-soccer-league"
                    style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box', fontFamily: 'monospace' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#4a5568', marginBottom: 4 }}>Excerpt (for list view & SEO)</label>
                <input
                  value={blogForm.excerpt}
                  onChange={(e) => setBlogForm(f => ({ ...f, excerpt: e.target.value }))}
                  placeholder="A short summary of the article..."
                  style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#4a5568', marginBottom: 4 }}>Content * (Markdown)</label>
                <textarea
                  value={blogForm.content}
                  onChange={(e) => setBlogForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="Write your article in Markdown..."
                  rows={16}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.88rem', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                <input
                  type="checkbox"
                  id="blog-published"
                  checked={blogForm.published}
                  onChange={(e) => setBlogForm(f => ({ ...f, published: e.target.checked }))}
                />
                <label htmlFor="blog-published" style={{ fontSize: '0.9rem', color: '#4a5568', cursor: 'pointer' }}>
                  Published (visible on /blog)
                </label>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  disabled={blogSaving || !blogForm.title.trim() || !blogForm.content.trim()}
                  onClick={async () => {
                    setBlogSaving(true);
                    try {
                      if (editingArticle) {
                        await api.updateBlogArticle(editingArticle.id, blogForm);
                      } else {
                        await api.createBlogArticle(blogForm);
                      }
                      setShowBlogForm(false);
                      setEditingArticle(null);
                      await loadBlogArticles();
                    } catch (err) {
                      showToast(err.message);
                    } finally {
                      setBlogSaving(false);
                    }
                  }}
                  style={{
                    padding: '0.5rem 1.25rem',
                    background: blogSaving || !blogForm.title.trim() || !blogForm.content.trim() ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: blogSaving || !blogForm.title.trim() || !blogForm.content.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  {blogSaving ? 'Saving...' : (editingArticle ? 'Save Changes' : 'Create Article')}
                </button>
                <button
                  onClick={() => { setShowBlogForm(false); setEditingArticle(null); }}
                  style={{ padding: '0.5rem 1rem', background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', cursor: 'pointer', color: '#718096' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Error / loading */}
          {blogLoading && <div style={{ color: '#718096', padding: '2rem 0' }}>Loading articles...</div>}
          {blogError && <div style={{ color: '#e53e3e', padding: '1rem 0' }}>{blogError}</div>}

          {/* Article list */}
          {!blogLoading && !showBlogForm && blogArticles && blogArticles.length === 0 && (
            <div style={{ color: '#718096', padding: '2rem 0', textAlign: 'center' }}>
              No articles yet. Click "+ New Article" to create the first one.
            </div>
          )}
          {!blogLoading && blogArticles && blogArticles.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem 0.75rem', color: '#4a5568', fontWeight: 600 }}>Title</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: '#4a5568', fontWeight: 600 }}>Slug</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: '#4a5568', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: '#4a5568', fontWeight: 600 }}>Date</th>
                  <th style={{ padding: '0.5rem 0.75rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {blogArticles.map(article => (
                  <tr key={article.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#2d3748', fontWeight: 500 }}>{article.title}</td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#718096', fontFamily: 'monospace', fontSize: '0.82rem' }}>{article.slug}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.2rem 0.6rem',
                        borderRadius: 99,
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        background: article.published ? '#c6f6d5' : '#e2e8f0',
                        color: article.published ? '#276749' : '#718096'
                      }}>
                        {article.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#718096', fontSize: '0.82rem' }}>
                      {article.publishedAt ? formatDate(article.publishedAt) : formatDate(article.createdAt)}
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => {
                          setEditingArticle(article);
                          setBlogForm({
                            title: article.title,
                            slug: article.slug,
                            excerpt: article.excerpt || '',
                            content: article.content || '',
                            published: article.published
                          });
                          setShowBlogForm(true);
                        }}
                        style={{ background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', marginRight: 8 }}
                      >
                        Edit
                      </button>
                      {blogDeleteId === article.id ? (
                        <>
                          <span style={{ fontSize: '0.82rem', color: '#e53e3e', marginRight: 6 }}>Delete?</span>
                          <button
                            onClick={async () => {
                              try {
                                await api.deleteBlogArticle(article.id);
                                setBlogDeleteId(null);
                                await loadBlogArticles();
                              } catch (err) {
                                showToast(err.message);
                              }
                            }}
                            style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', marginRight: 6 }}
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setBlogDeleteId(null)}
                            style={{ background: 'none', border: 'none', color: '#718096', cursor: 'pointer', fontSize: '0.85rem' }}
                          >
                            No
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setBlogDeleteId(article.id)}
                          style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Analytics Tab Component
// ============================================

function AnalyticsTab({ analytics, loading, error }) {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#666', fontSize: '1.1rem' }}>
        Loading analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#c62828' }}>
        {error}
      </div>
    );
  }

  if (!analytics) return null;

  const { platformOverview, usersByPlan, featureUsage, contentStats } = analytics;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {/* Key Metrics */}
      <div>
        <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>Platform Overview</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          <MetricCard
            label="Total Users"
            value={platformOverview.totalUsers}
            subtitle={`+${platformOverview.newUsersThisWeek} this week`}
          />
          <MetricCard
            label="Active Users"
            value={platformOverview.activeUsers}
            subtitle="Last 30 days"
          />
          <MetricCard
            label="Total Leagues"
            value={platformOverview.totalLeagues}
            subtitle={`${platformOverview.leaguesByStatus.draft} draft, ${platformOverview.leaguesByStatus.published} published`}
          />
          <MetricCard
            label="Games Played"
            value={platformOverview.totalGamesPlayed}
          />
        </div>
      </div>

      {/* Users by Plan */}
      <div>
        <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>Users by Plan</h3>
        <PlanBreakdown usersByPlan={usersByPlan} totalUsers={platformOverview.totalUsers} />
      </div>

      {/* Feature Usage */}
      <div>
        <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>Feature Usage</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '0.75rem'
        }}>
          <SmallMetricCard label="Captain Links" value={featureUsage.captainLinksGenerated} />
          <SmallMetricCard label="Emails Sent" value={featureUsage.emailsSent} />
          <SmallMetricCard label="Score Submissions" value={featureUsage.pendingScoresSubmitted} />
          <SmallMetricCard label="Seasons Archived" value={featureUsage.seasonsArchived} />
          <SmallMetricCard label="Venues Created" value={featureUsage.venuesCreated} />
          <SmallMetricCard label="Referees Created" value={featureUsage.refereesCreated} />
        </div>
      </div>

      {/* Top Leagues */}
      {contentStats.topLeagues.length > 0 && (
        <div>
          <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>Top Leagues</h3>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f7fafc' }}>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#4a5568', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>League Name</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#4a5568', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Teams</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#4a5568', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Games Played</th>
                </tr>
              </thead>
              <tbody>
                {contentStats.topLeagues.map((league, i) => (
                  <tr key={league.id} style={{ borderBottom: i < contentStats.topLeagues.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <td style={{ padding: '0.75rem 1rem', color: '#333', fontSize: '0.95rem' }}>{league.name}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#666', fontSize: '0.95rem' }}>{league.teamCount}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#666', fontSize: '0.95rem' }}>{league.gamesPlayed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Sub-components (all inline-styled)
// ============================================

function MetricCard({ label, value, subtitle }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '1.25rem 1.5rem',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    }}>
      <div style={{ color: '#718096', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ color: '#333', fontSize: '2rem', fontWeight: 700, lineHeight: 1.2 }}>{value.toLocaleString()}</div>
      {subtitle && (
        <div style={{ color: '#a0aec0', fontSize: '0.8rem', marginTop: '0.25rem' }}>{subtitle}</div>
      )}
    </div>
  );
}

function SmallMetricCard({ label, value }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      padding: '1rem 1.25rem',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    }}>
      <div style={{ color: '#718096', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ color: '#333', fontSize: '1.5rem', fontWeight: 700 }}>{value.toLocaleString()}</div>
    </div>
  );
}

function PlanBreakdown({ usersByPlan, totalUsers }) {
  const plans = [
    { key: 'free', label: 'Free', color: '#a0aec0', bg: '#edf2f7' },
    { key: 'starter', label: 'Starter', color: '#764ba2', bg: '#f3e8ff' },
    { key: 'pro', label: 'Pro', color: '#667eea', bg: '#ebf4ff' }
  ];

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '1.25rem 1.5rem',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    }}>
      {/* Bar chart */}
      <div style={{
        display: 'flex',
        borderRadius: '8px',
        overflow: 'hidden',
        height: '32px',
        marginBottom: '1rem',
        background: '#f7fafc'
      }}>
        {plans.map(plan => {
          const count = usersByPlan[plan.key] || 0;
          const pct = totalUsers > 0 ? (count / totalUsers) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={plan.key}
              style={{
                width: `${pct}%`,
                minWidth: pct > 0 ? '24px' : '0',
                background: plan.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 600,
                transition: 'width 0.3s ease'
              }}
              title={`${plan.label}: ${count}`}
            >
              {pct >= 10 ? `${Math.round(pct)}%` : ''}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        {plans.map(plan => (
          <div key={plan.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: plan.color }} />
            <span style={{ color: '#4a5568', fontSize: '0.9rem' }}>
              {plan.label}: <strong>{usersByPlan[plan.key] || 0}</strong>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminPage;
